import { httpClient } from '@/shared/api/httpClient';

// =====================================================================
// Avaliações (feedback) do cliente da oficina. O cliente recebe pelo
// pós-venda o link público `/avaliar/<token>` (variável {{avaliacao}}),
// dá de 1 a 5 estrelas e deixa um comentário. Os dados moram no servidor
// (pós-venda é online por natureza): no desktop estas rotas passam pelo
// IPC e viram proxy para o servidor; no web vão direto para /app/api.
// =====================================================================

export type Nota = 1 | 2 | 3 | 4 | 5;

/** Avaliação ligada a uma O.S. (card dentro da ordem de serviço). */
export type AvaliacaoOS = {
  id: string;
  orcamentoId: string;
  /** null enquanto o cliente ainda não respondeu (link gerado, sem resposta). */
  nota: Nota | null;
  comentario: string | null;
  /** Quando o link foi gerado. */
  criadoEm: string;
  respondidoEm: string | null;
};

/**
 * Nota de uma O.S. já respondida, para os cards do kanban. `trecho` é o
 * comentário já encurtado pelo servidor (~80 caracteres); null se não houver.
 */
export type NotaOS = {
  orcamentoId: string;
  nota: Nota;
  trecho: string | null;
};

export type ClassificacaoAvaliacao = 'todas' | 'boas' | 'ruins';

export type FiltroAvaliacoes = {
  /** YYYY-MM-DD — filtra por data da resposta. */
  inicio?: string;
  fim?: string;
  /** Uma ou mais notas exatas. */
  notas?: Nota[];
  classificacao?: ClassificacaoAvaliacao;
  comComentario?: boolean;
  responsavelId?: string;
  /** Busca livre em cliente, número da O.S., placa e comentário. */
  busca?: string;
};

export type ItemAvaliacao = {
  id: string;
  orcamentoId: string;
  orcamentoNumero: string | null;
  osData: string | null;
  clienteId: string | null;
  clienteNome: string | null;
  clienteTelefone: string | null;
  veiculo: string | null;
  placa: string | null;
  responsavelId: string | null;
  responsavelNome: string | null;
  nota: Nota;
  comentario: string | null;
  respondidoEm: string;
};

export type RelatorioAvaliacoes = {
  /** Nota abaixo deste valor conta como "ruim" (2 a 5; padrão 3). */
  limiteRuim: number;
  /**
   * Indicadores do período (e do responsável, se filtrado). Ignoram os
   * filtros de nota/classificação/comentário/busca, que só afetam `itens`.
   */
  resumo: {
    media: number | null;
    totalRespostas: number;
    boas: number;
    ruins: number;
    comComentario: number;
    /** Links de avaliação gerados no período (por criadoEm). */
    linksGerados: number;
    /** respostas ÷ links gerados no período (0..1); null sem links. */
    taxaResposta: number | null;
    distribuicao: Record<'1' | '2' | '3' | '4' | '5', number>;
  };
  porResponsavel: Array<{ responsavelId: string | null; nome: string; quantidade: number; media: number }>;
  porMes: Array<{ mes: string; quantidade: number; media: number }>;
  /** Mais recentes primeiro. */
  itens: ItemAvaliacao[];
};

function montarQuery(f: FiltroAvaliacoes): string {
  const p = new URLSearchParams();
  if (f.inicio) p.set('inicio', f.inicio);
  if (f.fim) p.set('fim', f.fim);
  if (f.notas?.length) p.set('notas', f.notas.join(','));
  if (f.classificacao && f.classificacao !== 'todas') p.set('classificacao', f.classificacao);
  if (f.comComentario) p.set('comComentario', 'true');
  if (f.responsavelId) p.set('responsavelId', f.responsavelId);
  if (f.busca?.trim()) p.set('busca', f.busca.trim());
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const avaliacoesApi = {
  /** Avaliação da O.S. (ou null se nunca foi gerado link). Não gera link. */
  daOS: (orcamentoId: string) =>
    httpClient.get<{ avaliacao: AvaliacaoOS | null }>(
      `/api/pos-venda/avaliacoes/os/${encodeURIComponent(orcamentoId)}`,
    ),
  /** Gera (ou reaproveita) o link público de avaliação da O.S. Exige pós-venda ativo. */
  gerarLink: (orcamentoId: string) =>
    httpClient.post<{ link: string }>(
      `/api/pos-venda/avaliacoes/os/${encodeURIComponent(orcamentoId)}/link`,
      {},
    ),
  /**
   * Notas das avaliações respondidas, em lote. Sem `ids` (ou vazio) traz
   * todas as respondidas da conta. Só leitura — não exige pós-venda ativo.
   */
  notas: (ids?: string[]) =>
    httpClient.get<{ notas: NotaOS[] }>(
      `/api/pos-venda/avaliacoes/notas${ids?.length ? `?ids=${encodeURIComponent(ids.join(','))}` : ''}`,
    ),
  relatorio: (filtro: FiltroAvaliacoes = {}) =>
    httpClient.get<RelatorioAvaliacoes>(`/api/pos-venda/avaliacoes${montarQuery(filtro)}`),
  /** "Considerar ruim abaixo de N estrelas" (2 a 5). */
  salvarLimite: (limiteRuim: number) =>
    httpClient.put<{ limiteRuim: number }>('/api/pos-venda/avaliacoes/limite', { limiteRuim }),
};
